import { format }    from 'date-fns';
import currency      from 'currency.js';
import Immutable     from 'immutable';
import React         from 'react';
import toMoneyFormat from '../util/to_money_format';


export default function PayoutsList({ payouts }) {
  const pendingTransactions       = payouts?.pendingTransactions;
  const payoutTransactions        = payouts?.payouts;
  const hasEstimatedFuturePayouts = pendingTransactions?.length > 0;
  const hasPayoutTransactions     = payoutTransactions?.length > 0;

  return (
    <div className="payouts">
      {hasEstimatedFuturePayouts && <FuturePayouts {...{ transactions: pendingTransactions }}/>}
      {hasPayoutTransactions && <Payouts {...{ payouts: payoutTransactions }}/>}
      {!hasEstimatedFuturePayouts && !hasPayoutTransactions && <NoPayouts/>}
    </div>
  );
}


function FuturePayouts({ transactions }) {
  const transactionsByDate = Immutable.List(transactions)
    .groupBy(t => t.availableOn);

  return (
    <div className="block payouts__future-payouts">
      <div className="headline">
        ESTIMATED FUTURE PAYOUTS
      </div>

      {transactionsByDate.map((trs, date) => FuturePayout({ date, transactions: trs })).valueSeq()}
    </div>
  );
}


function Payouts({ payouts }) {
  return (
    <div className="block payouts__payouts">
      <div className="headline">
        LATEST PAYOUTS
      </div>

      {payouts.map(payout => <Payout key={payout.id} {...{ payout }}/>)}
    </div>
  );
}


function FuturePayout({ date, transactions }) {
  const formattedDate  = getLocalDateFormattedToUTC(new Date(date));
  const estimatedTotal = transactions
    .map(({ net }) => net)
    .reduce((total, net) => total.add(net), currency())
    .toString();

  return (
    <div className="payouts__payout" key={`pending_${date}}`}>
      <div className="row">
        <div className="col-75">
          Payout expected to arrive {formattedDate}
        </div>
        <div className="payout--total col-25 text-align-right">
          {toMoneyFormat({ amount: estimatedTotal })}
        </div>
      </div>
      <div className="payouts__transactions">
        {transactions.map(transaction => <Transaction key={transaction.id} {...{ transaction }}/>)}
      </div>
    </div>
  );
}


function Payout({ payout }) {
  const formattedDate    = getLocalDateFormattedToUTC(new Date(payout.arrivalDate));
  const { amount }       = payout;
  const { transactions } = payout;
  const { status }       = payout;
  const label            = (status === 'in_transit' ? 'In Transit' : null);

  return (
    <div className="payouts__payout">
      <div className="row">
        <div className="col-75">
          {formattedDate}
          {label && <span className="badge badge-warn">{label}</span>}
        </div>
        <div className="col-25 text-align-right">
          {toMoneyFormat({ amount })}
        </div>
      </div>

      <div className="payouts__transactions">
        {transactions.map(transaction => <Transaction key={transaction.id} {...{ transaction }}/>)}
      </div>
    </div>
  );
}


function Transaction({ transaction }) {
  const { type }        = transaction;
  const { invoice }     = transaction;
  const { customer }    = invoice;
  const { description } = invoice;
  const customerName    = customer?.name.full;
  const invoiceURL      = `/${invoice.business.id}/metrics/invoices/${invoice.id}`;
  const transactionType = getTransacionType(type);

  return (
    <div className="payouts__transaction row">
      <div className="col-75 truncate">
        <a href={invoiceURL}>{customerName || `Invoice #${invoice.number}`}</a>
        {transactionType && ` (${transactionType})`}
        {description ? ` – ${description}` : null}
      </div>
      <div className="col-25 text-align-right">
        {toMoneyFormat({ amount: transaction.net })}
      </div>
    </div>
  );
}


function getTransacionType(type) {
  switch (type) {
    case 'refund':
      return 'Refund';
    case 'fee_refund':
      return 'Fee Refund';
    default:
      return null;
  }
}


// Stripe payouts are processed on UTC time. This function creates a new date
// based on the timezone offest of the user.
//
// https://stripe.com/docs/payouts#payout-schedule
//
function getLocalDateFormattedToUTC(date) {
  const dateOnly = new Date(date.valueOf() + date.getTimezoneOffset() * 60 * 1000);
  return format(dateOnly, 'iiii, MMMM d yyyy');
}


function NoPayouts() {
  return (
    <div className="block">
      <div className="empty-page">
        <div className="empty-page__note">
          No payouts to show
        </div>
        <div className="empty-page__icons">
          <i className="empty-page__icon icons8 icons8-electrical"></i>
        </div>
      </div>
    </div>
  );
}
